/**
 * Session F (sandbox only): wires the cart drawer's checkout button to
 * Paddle.Checkout.open. Reads each cart row's `data-paddle-price-id`
 * (rendered server-side from `product.metafields.custom.paddle_price_id`
 * in snippets/cart-products.liquid) rather than calling Shopify again, and
 * passes Build Your Own's chosen pattern product ids through `customData` —
 * the Worker's webhook resolver needs those to know exactly which patterns
 * were picked (brief: "store the chosen patterns ... passed to Paddle
 * customData so My Pattern Library unlocks exactly those patterns").
 *
 * Mixed-pattern discount (Native-Cart brief, section 5): every cart row
 * carries `data-pattern-weight` — 1 for a single pattern, 2 for a pattern set
 * (custom.pick_weight), 0 for bundles and Build Your Own. The weighted count
 * picks the tier:
 *   2       -> 15% off (Paddle discount from the Worker's /checkout/pricing)
 *   3 or 4+ -> 25% off, plus a next-goal line toward the 5-pattern price
 *   5/10/15 -> swap the patterns for the Build Your Own price when that is
 *              cheaper for the buyer. A set fills 2 of the picks.
 * The discounts are restricted to pattern prices in Paddle, so bundles never
 * get them.
 *
 * The cart shows exactly what Paddle will charge before tax ("Patterns /
 * Your savings / Total"): the same plan that goes to checkout is priced here
 * with Paddle's own rounding (each line's discount rounded to the cent), and
 * then confirmed with Paddle.TransactionPreview when Paddle.js is ready.
 *
 * Every Paddle price allows quantity 1 only (digital patterns), so checkout
 * always sends quantity 1, and a cart line that somehow reached quantity 2+
 * is set back to 1 with an "Already in your cart" note (+ gold star).
 *
 * If the pricing request fails, it is retried once. If it still fails, the
 * drawer shows a retry message and checkout does NOT open at full price.
 *
 * Uses event delegation on `document` rather than wiring the #checkout
 * button directly: the cart drawer section re-renders (morphs in a fresh
 * button node) on every add/remove, which silently drops a listener
 * attached to the old node. Delegation survives that because it never
 * depends on which concrete node is currently in the DOM.
 */
const PRICING_URL = 'https://litelier-patterns-worker.litelier.workers.dev/checkout/pricing';

let pricingPromise = null;

const PRICING_CACHE_KEY = 'litelier-pricing';
const PRICING_CACHE_MS = 10 * 60 * 1000;

function readCachedPricing() {
  try {
    const cached = JSON.parse(window.localStorage.getItem(PRICING_CACHE_KEY) || 'null');
    return cached && Date.now() - cached.at < PRICING_CACHE_MS && cached.data?.tiers ? cached.data : null;
  } catch {
    return null;
  }
}

async function fetchPricingOnce() {
  const response = await fetch(PRICING_URL);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  try {
    window.localStorage.setItem(PRICING_CACHE_KEY, JSON.stringify({ at: Date.now(), data }));
  } catch {
    // storage unavailable: the Worker's edge cache still keeps this fast
  }
  return data;
}

/**
 * Fetched once per page. Retried once on failure; a page that still fails
 * gets null, and the next checkout click starts a fresh attempt.
 */
function getPricing() {
  // A fresh copy from an earlier page view (≤ 10 min): totals appear at once, no network wait.
  const cached = !pricingPromise && readCachedPricing();
  if (cached) pricingPromise = Promise.resolve(cached);
  if (!pricingPromise) {
    pricingPromise = fetchPricingOnce().catch(async (firstError) => {
      console.warn('Checkout pricing request failed, retrying once:', firstError);
      try {
        return await fetchPricingOnce();
      } catch (error) {
        console.error('Checkout pricing unavailable:', error);
        pricingPromise = null;
        return null;
      }
    });
  }
  return pricingPromise;
}

function readCartRows() {
  return [...document.querySelectorAll('[data-key][data-paddle-price-id]')].map((row) => {
    const quantity = Number(row.getAttribute('data-quantity')) || 1;
    const lineCents = Number(row.getAttribute('data-line-cents')) || 0;
    return {
      row,
      key: row.getAttribute('data-key'),
      priceId: row.getAttribute('data-paddle-price-id'),
      productId: row.getAttribute('data-product-id'),
      title: row.getAttribute('data-product-title') || 'This pattern',
      // What the shopper sees in the cart line, not what checkout uses: checkout is always 1.
      cartQuantity: quantity,
      quantity: 1,
      weight: Number(row.getAttribute('data-pattern-weight')) || 0,
      // One unit's list price; the line price divided back when an older cart has quantity 2+.
      lineCents: Number(row.getAttribute('data-unit-cents')) || Math.round(lineCents / quantity),
      byob: row.getAttribute('data-build-your-own'),
    };
  });
}

/** Patterns counted toward the tiers: a single counts 1, a set 2. Bundles and Build Your Own count 0. */
function countWeightedPatterns(rows) {
  return rows.reduce((total, r) => total + r.weight, 0);
}

/** Percent off the patterns for a given weighted count. */
function percentForCount(weighted) {
  if (weighted >= 3) return 25;
  if (weighted === 2) return 15;
  return 0;
}

function formatCents(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Build Your Own bundles that can absorb patterns. One bundle per transaction:
 * customData carries a single build_your_own list, so two bundles can't be
 * described to the Worker.
 */
const BYO_BUNDLE_SIZES = [5, 10, 15];

/** Paddle takes a percentage discount off each line and rounds it to the cent. */
function discountedCents(unitCents, pct) {
  return unitCents - Math.round((unitCents * pct) / 100);
}

/**
 * The patterns for one bundle: most expensive first, up to `size` picks (a set = 2).
 * A bundle may hold fewer than `size` (price cap: 4 patterns never cost more
 * than the 5-pattern price; the buyer is told the 5th is free).
 */
function pickBundlePatterns(eligible, size) {
  const byPriceDesc = [...eligible].sort((a, b) => b.lineCents - a.lineCents);
  const chosen = [];
  let remaining = size;
  for (const row of byPriceDesc) {
    if (row.weight <= remaining) {
      chosen.push(row);
      remaining -= row.weight;
      if (remaining === 0) break;
    }
  }
  return chosen.length > 0 ? { chosen, filled: size - remaining } : null;
}

/**
 * Cheapest combination (Gina, Oct 9: a cart never costs more than the next
 * Build Your Own price): no bundle (tier discount on everything), or one
 * bundle, full or not, plus the leftover patterns at the discount their own
 * count earns. Ties keep the simpler no-bundle option.
 */
function cheapestPlan(eligible, weighted, pricing) {
  const plainPct = percentForCount(weighted);
  const plainCents = eligible.reduce((total, r) => total + discountedCents(r.lineCents, plainPct), 0);
  let best = { chosen: [], bundleSize: 0, bundleFilled: 0, pct: plainPct, costCents: plainCents };

  for (const size of BYO_BUNDLE_SIZES) {
    if (!pricing.byo[String(size)]) continue;
    const picked = pickBundlePatterns(eligible, size);
    if (!picked) continue;
    const { chosen, filled } = picked;
    const leftover = eligible.filter((r) => !chosen.includes(r));
    const pct = percentForCount(weighted - filled);
    const leftoverCents = leftover.reduce((total, r) => total + discountedCents(r.lineCents, pct), 0);
    const bundleCents = Math.round(Number(pricing.byoListPrice[String(size)]) * 100);
    const costCents = bundleCents + leftoverCents;
    if (costCents < best.costCents) best = { chosen, bundleSize: size, bundleFilled: filled, pct, costCents };
  }
  return best;
}

/**
 * The one plan used for both the cart totals and checkout: Paddle items,
 * discount, Build Your Own custom data, and the totals in cents before tax.
 * Without pricing (request failed) it still prices the cart, but `discountId`
 * is missing, and checkout refuses to open (see openPaddleCheckoutForCart).
 */
function planCart(rows, pricing) {
  const eligible = rows.filter((r) => r.weight > 0);
  const others = rows.filter((r) => r.weight === 0);
  const weighted = countWeightedPatterns(rows);
  const existingByo = rows.find((r) => r.byob);

  let buildYourOwn = null;
  if (existingByo) {
    try {
      buildYourOwn = JSON.parse(existingByo.byob);
    } catch {
      console.error('Could not parse data-build-your-own JSON:', existingByo.byob);
    }
  }

  // Swap patterns for a Build Your Own price only when the cart has no Build Your Own line already.
  const best =
    pricing && !existingByo
      ? cheapestPlan(eligible, weighted, pricing)
      : (() => {
          const pct = percentForCount(weighted);
          return { chosen: [], bundleSize: 0, bundleFilled: 0, pct, costCents: eligible.reduce((t, r) => t + discountedCents(r.lineCents, pct), 0) };
        })();

  const leftover = eligible.filter((r) => !best.chosen.includes(r));
  // One discount per transaction, restricted to pattern prices. It covers only the patterns left outside the bundle.
  const discountId = pricing && leftover.length > 0 && best.pct ? pricing.tiers[String(best.pct)] : undefined;
  const othersCents = others.reduce((total, r) => total + r.lineCents, 0);
  const listCents = othersCents + eligible.reduce((total, r) => total + r.lineCents, 0);
  const totalCents = othersCents + best.costCents;

  const items =
    best.bundleSize === 0
      ? rows.map((r) => ({ priceId: r.priceId, quantity: 1 }))
      : [
          ...others.map((r) => ({ priceId: r.priceId, quantity: 1 })),
          { priceId: pricing.byo[String(best.bundleSize)], quantity: 1 },
          ...leftover.map((r) => ({ priceId: r.priceId, quantity: 1 })),
        ];

  return {
    items,
    discountId,
    discountNeeded: best.pct > 0 && leftover.length > 0,
    buildYourOwn: best.bundleSize === 0 ? buildYourOwn : best.chosen.map((r) => r.productId),
    weighted,
    listCents,
    totalCents,
    // Price cap in use: a Build Your Own bundle with free picks left ("your 5th pattern is free").
    freePicks: best.bundleSize > 0 ? best.bundleSize - best.bundleFilled : 0,
    bundleSize: best.bundleSize,
    // Patterns charged outside the bundle (e.g. a 2-piece set that doesn't fit the last slot).
    leftoverCount: best.bundleSize > 0 ? leftover.length : 0,
  };
}

/**
 * Paddle's own price for a plan, before tax (subtotal minus discount), or
 * null when Paddle.js isn't initialized yet, the preview fails, or it takes
 * longer than 4 s. Only finished answers are cached. (A preview requested
 * before Paddle.Initialize never settles, so it must not be asked early.)
 */
const PREVIEW_TIMEOUT_MS = 4000;
const previewCache = new Map();

function paddlePreviewCents(plan) {
  if (!window.Paddle?.Initialized || !window.Paddle.TransactionPreview || !plan.items.every((i) => i.priceId)) return Promise.resolve(null);
  const key = JSON.stringify([plan.items, plan.discountId ?? null]);
  if (!previewCache.has(key)) {
    const timeout = new Promise((resolve) => window.setTimeout(() => resolve('timeout'), PREVIEW_TIMEOUT_MS));
    const preview = window.Paddle.TransactionPreview({
      items: plan.items,
      ...(plan.discountId ? { discountId: plan.discountId } : {}),
    });
    const request = Promise.race([preview, timeout])
      .then((result) => {
        if (result === 'timeout') {
          previewCache.delete(key);
          return null;
        }
        const totals = result?.data?.details?.totals;
        if (!totals) return null;
        return Number(totals.subtotal) - Number(totals.discount);
      })
      .catch((error) => {
        console.warn('Paddle price preview failed, showing the cart estimate:', error);
        previewCache.delete(key);
        return null;
      });
    previewCache.set(key, request);
  }
  return previewCache.get(key);
}

/**
 * Cart totals in cents before tax. The local calculation uses Paddle's own
 * rounding; with `confirm`, Paddle's preview replaces it when available.
 */
async function cartTotals(allRows, { confirm = true } = {}) {
  // Products without a Paddle price can't be bought, so they don't count toward totals or tiers.
  const rows = allRows.filter((r) => r.priceId);
  const weighted = countWeightedPatterns(rows);
  const pricing = rows.some((r) => r.weight > 0) ? await getPricing() : null;
  const plan = planCart(rows, pricing);
  const fromPaddle = confirm ? await paddlePreviewCents(plan) : null;
  const totalCents = fromPaddle ?? plan.totalCents;
  return { plan, pricing, weighted, listCents: plan.listCents, totalCents, savingsCents: Math.max(0, plan.listCents - totalCents) };
}

/** Used by the launch/welcome gift (assets/launch-gift.js) for the $9.95 line; the Worker re-checks after payment. */
async function estimateCartCents(rows) {
  return (await cartTotals(rows)).totalCents;
}

// ---------- savings bar + totals in the cart ----------

const BUNDLE_FALLBACK_PRICES = { 5: '14.95', 10: '24.95', 15: '32.95' };

function escapeHtml(text) {
  return String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

/** Brand icon since Oct 9 (Voice Guide): rounded gold star, used in new wording. Same as assets/icon-star-rounded.svg. */
const STAR = '<svg class="brand-star" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false"><path d="M12 3.6l2.5 5.2 5.7.8-4.1 4 1 5.7L12 16.6l-5.1 2.7 1-5.7-4.1-4 5.7-.8z" fill="#C8922C" stroke="#C8922C" stroke-width="2.2" stroke-linejoin="round"/></svg>';

/** Savings-bar wording (main chat, Oct 9). Returns HTML (the 15+ line carries a link) and the bar fill. */
function tierCopy(n, pricing, library, plan) {
  // Price cap (Oct 9 wording): the cart is charged a Build Your Own price with picks to spare.
  // Gina, Oct 10: "Your 5th pattern is free ★ Choose 1 more"; 2+ free: "Your next 3 patterns are free ★ Choose 3 more".
  // Only when every pattern is inside the bundle: with 3 sets (6 patterns) the 5-bundle holds
  // 2 sets and the 3rd is charged outside, so "Choose 1 more" would be wrong (Gina, Oct 16).
  if (plan && plan.freePicks > 0 && !plan.leftoverCount) {
    if (plan.freePicks === 1) return { html: `Your ${plan.bundleSize}th pattern is free ${STAR} Choose 1 more`, fill: 1 };
    return { html: `Your next ${plan.freePicks} patterns are free ${STAR} Choose ${plan.freePicks} more`, fill: 1 };
  }
  const price = (size) => `$${pricing?.byoListPrice?.[String(size)] ?? BUNDLE_FALLBACK_PRICES[size]}`;
  const heart = '<span class="lk-star" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false"><path d="M12 3.6l2.5 5.2 5.7.8-4.1 4 1 5.7L12 16.6l-5.1 2.7 1-5.7-4.1-4 5.7-.8z" fill="#C8922C" stroke="#C8922C" stroke-width="2.2" stroke-linejoin="round"/></svg></span>';
  if (n <= 0) return null;
  if (n === 1) return { html: `Add 1 more pattern and save 15% ${heart}`, fill: 1 / 3 };
  if (n === 2) return { html: `You’re saving 15% ${heart} Add 1 more and save 25%`, fill: 2 / 3 };
  if (n <= 4) return { html: `You’re saving 25% ${heart} Add ${5 - n} more and get all 5 for ${price(5)}`, fill: n / 5 };
  if (n === 5) return { html: `5 patterns for ${price(5)} ${heart} Add 5 more and get all 10 for ${price(10)}`, fill: 1 };
  if (n <= 9) return { html: `Best price applied ${heart} Add ${10 - n} more and get all 10 for ${price(10)}`, fill: 1 };
  if (n === 10) return { html: `10 patterns for ${price(10)} ${heart} Add 5 more and get all 15 for ${price(15)}`, fill: 1 };
  if (n <= 14) return { html: `Best price applied ${heart} Add ${15 - n} more and get all 15 for ${price(15)}`, fill: 1 };
  const link = library.url
    ? `<a href="${escapeHtml(library.url)}">The Complete Library is ${escapeHtml(library.price)}</a>`
    : `The Complete Library is ${escapeHtml(library.price)}`;
  return { html: `Best price applied ${heart} Want everything? ${link}`, fill: 1 };
}

function noteHtml(rows) {
  let text = 'Your savings apply automatically';
  if (rows.some((r) => r.weight === 2)) text += ' (a 2-piece set counts as 2 patterns)';
  text += '.';
  if (rows.some((r) => r.weight === 0)) text += ' Bundles already include their own savings.';
  return text;
}

function setText(element, text) {
  if (element && element.textContent !== text) element.textContent = text;
}

function setInnerHtml(element, html) {
  if (element && (element.dataset.rendered !== html || !element.firstChild)) {
    element.dataset.rendered = html;
    element.innerHTML = html;
  }
}

function showTierError(visible) {
  for (const error of document.querySelectorAll('[data-cart-tier-error]')) error.hidden = !visible;
  if (visible) for (const box of document.querySelectorAll('[data-cart-tier]')) box.hidden = false;
}

let renderToken = 0;

/** Savings bar, notes and "Patterns / Your savings / Total". Safe to call on every cart re-render. */
async function renderCartSummary() {
  const token = ++renderToken;
  const rows = readCartRows();
  const boxes = [...document.querySelectorAll('[data-cart-tier]')];
  const totalsBlocks = [...document.querySelectorAll('[data-litelier-totals]')];
  if (boxes.length === 0 && totalsBlocks.length === 0) return;

  if (rows.length === 0) {
    for (const box of boxes) box.hidden = true;
    return;
  }

  // Show the local figure straight away, then Paddle's confirmation if it differs.
  const local = await cartTotals(rows, { confirm: false });
  if (token !== renderToken) return; // a newer render started while this one waited
  writeSummary(rows, local);
  const confirmed = await cartTotals(rows);
  if (token !== renderToken || confirmed.totalCents === local.totalCents) return;
  writeSummary(rows, confirmed);
}

/** Looks the boxes up again: the drawer may have swapped them while prices loaded. */
function writeSummary(rows, totals) {
  const boxes = [...document.querySelectorAll('[data-cart-tier]')];
  const totalsBlocks = [...document.querySelectorAll('[data-litelier-totals]')];
  for (const box of boxes) {
    const copy = tierCopy(totals.weighted, totals.pricing, {
      url: box.getAttribute('data-library-url'),
      price: box.getAttribute('data-library-price') || '$49.95',
    }, totals.plan);
    const message = box.querySelector('[data-cart-tier-message]');
    const bar = box.querySelector('.cart-tier__bar');
    const fill = box.querySelector('[data-cart-tier-fill]');
    if (message) {
      if (message.hidden === Boolean(copy)) message.hidden = !copy;
      if (copy) setInnerHtml(message, copy.html);
    }
    if (bar && bar.hidden === Boolean(copy)) bar.hidden = !copy;
    if (fill && copy) {
      const width = `${Math.round(copy.fill * 100)}%`;
      if (fill.style.width !== width) fill.style.width = width;
    }
    setInnerHtml(box.querySelector('[data-cart-tier-note]'), noteHtml(rows));
    if (box.hidden) box.hidden = false;
  }

  for (const block of totalsBlocks) {
    setText(block.querySelector('[data-litelier-list]'), formatCents(totals.listCents));
    setText(block.querySelector('[data-litelier-savings]'), `−${formatCents(totals.savingsCents)}`);
    setText(block.querySelector('[data-litelier-total]'), formatCents(totals.totalCents));
    const savingsRow = block.querySelector('[data-litelier-savings-row]');
    if (savingsRow && savingsRow.hidden === totals.savingsCents > 0) savingsRow.hidden = totals.savingsCents === 0;
    if (block.hidden) block.hidden = false;
    // The theme's own total ignores the Paddle discounts, so it hides once ours is shown.
    const native = block.closest('.cart-totals')?.querySelector('[data-native-total]');
    if (native && !native.hidden) native.hidden = true;
  }
}

// ---------- one of each pattern ----------

function showAlreadyInCartNote() {
  let note = document.querySelector('[data-cart-duplicate-note]');
  if (!note) {
    note = document.createElement('div');
    note.className = 'cart-duplicate-note';
    note.setAttribute('data-cart-duplicate-note', '');
    note.setAttribute('role', 'status');
    document.body.appendChild(note);
  }
  note.innerHTML = 'Already in your cart <span class="lk-star" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false"><path d="M12 3.6l2.5 5.2 5.7.8-4.1 4 1 5.7L12 16.6l-5.1 2.7 1-5.7-4.1-4 5.7-.8z" fill="#C8922C" stroke="#C8922C" stroke-width="2.2" stroke-linejoin="round"/></svg></span>';
  note.classList.add('cart-duplicate-note--visible');
  window.clearTimeout(showAlreadyInCartNote.timer);
  showAlreadyInCartNote.timer = window.setTimeout(() => note.classList.remove('cart-duplicate-note--visible'), 2600);
}

const normalizingKeys = new Set();

/**
 * A pattern is always quantity 1. The add-to-cart guard (cart-no-duplicate-guard.js)
 * normally stops a second add; if a line still reaches 2+ (quick add, a fast
 * double tap, an older cart), set it back to 1 through the cart component.
 */
async function normalizeQuantities() {
  if (!document.querySelector('cart-items-component [data-quantity]:not([data-quantity="1"])')) return;
  // The cart component's module may still be loading on first paint.
  await customElements.whenDefined('cart-items-component');
  for (const component of document.querySelectorAll('cart-items-component')) {
    const rows = [...component.querySelectorAll('[data-key][data-quantity]')];
    rows.forEach((row, index) => {
      const key = row.getAttribute('data-key');
      if (Number(row.getAttribute('data-quantity')) <= 1 || normalizingKeys.has(key)) return;
      if (typeof component.updateQuantity !== 'function') return;
      normalizingKeys.add(key);
      component.updateQuantity({ line: index + 1, quantity: 1, action: 'change' });
      showAlreadyInCartNote();
      window.setTimeout(() => normalizingKeys.delete(key), 4000);
    });
  }
}

// planCart, getPricing and tierCopy are exposed for price checks against Paddle (no side effects).
window.__litelierCart = { readCartRows, estimateCartCents, planCart, getPricing, tierCopy };
document.dispatchEvent(new Event('litelier:cart-ready'));

// ---------- checkout ----------

document.addEventListener('click', (event) => {
  const retry = event.target.closest('[data-cart-tier-retry]');
  if (retry) {
    event.preventDefault();
    pricingPromise = null;
    const checkout = document.querySelector('#checkout');
    if (checkout instanceof HTMLButtonElement) openPaddleCheckoutForCart(checkout);
    return;
  }

  const button = event.target.closest('#checkout');
  if (!(button instanceof HTMLButtonElement)) return;
  event.preventDefault();
  openPaddleCheckoutForCart(button);
});

async function openPaddleCheckoutForCart(button) {
  if (!window.Paddle) {
    console.error('Paddle.js has not loaded yet');
    return;
  }

  const rows = readCartRows();
  if (rows.length === 0) {
    console.error('No cart rows with a Paddle price id found — is the cart empty, or missing custom.paddle_price_id?');
    return;
  }

  // A product without a Paddle price can't be sold: say which one, instead of doing nothing.
  const missingPrice = rows.filter((r) => !r.priceId);
  for (const message of document.querySelectorAll('[data-cart-missing-price]')) {
    // Wording approved by the main chat (Oct 9).
    message.innerHTML = missingPrice
      .map((r) => `“${escapeHtml(r.title)}” isn’t available right now. Please remove it to check out <span class="lk-star" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false"><path d="M12 3.6l2.5 5.2 5.7.8-4.1 4 1 5.7L12 16.6l-5.1 2.7 1-5.7-4.1-4 5.7-.8z" fill="#C8922C" stroke="#C8922C" stroke-width="2.2" stroke-linejoin="round"/></svg></span>`)
      .join('<br>');
    message.hidden = missingPrice.length === 0;
  }
  if (missingPrice.length > 0) {
    console.error('These cart products have no paddle_price_id metafield set, so checkout cannot include them:', missingPrice.map((r) => r.productId));
    return;
  }

  const originalText = button.textContent;
  button.disabled = true;
  button.textContent = 'Opening checkout…';

  try {
    const pricing = rows.some((r) => r.weight > 0) ? await getPricing() : null;
    const plan = planCart(rows, pricing);
    if (rows.some((r) => r.weight > 0) && !pricing) {
      // Never open checkout at full price: show the retry message instead.
      showTierError(true);
      return;
    }
    showTierError(false);

    // Paddle's customData validation rejects the field entirely if any value
    // is `undefined` — only include keys that actually have a value.
    const cartToken = window.Shopify?.cart?.token;
    const customData = {};
    if (cartToken) customData.cart_token = cartToken;
    if (plan.buildYourOwn) customData.build_your_own = plan.buildYourOwn;
    // Launch / welcome gift: the chosen pattern id and welcome link travel as custom data, never as a line item.
    const gift = window.__litelierGift ? await window.__litelierGift.checkoutData(rows) : null;
    if (gift) customData.gift = gift;
    // CC-TODAY 168a: where the buyer came from (first + last touch, assets/lk-source.js). Never blocks checkout.
    const source = sourceForCheckout();
    if (source) customData.source = source;

    closeCartDrawer();
    window.Paddle.Checkout.open({
      items: plan.items,
      ...(plan.discountId ? { discountId: plan.discountId } : {}),
      ...(Object.keys(customData).length > 0 ? { customData } : {}),
      ...(window.__litelierCustomerEmail ? { customer: { email: window.__litelierCustomerEmail } } : {}),
      settings: {
        displayMode: 'overlay',
        // Paddle requires an absolute URL — __litelierLibraryUrl is a
        // Shopify-relative path (e.g. "/pages/my-pattern-library").
        // T-455: our own "?paid=1", never "_ptxn" (Paddle.js reopens the checkout when it sees _ptxn).
        successUrl: new URL(`${window.__litelierLibraryUrl || '/pages/my-pattern-library'}?paid=1`, window.location.origin).href,
      },
    });
  } catch (error) {
    console.error('Could not open checkout:', error);
    showTierError(true);
  } finally {
    button.disabled = false;
    button.textContent = originalText;
  }
}

/** First/last touch from lk-source.js with empty values left out (Paddle rejects undefined), or null. */
function sourceForCheckout() {
  try {
    const raw = typeof window.__litelierSource === 'function' ? window.__litelierSource() : null;
    if (!raw) return null;
    const keep = (t) => {
      const out = {};
      for (const k of ['src', 'utm_source', 'utm_medium', 'utm_campaign', 'referrer', 'landing', 'at']) {
        if (t && typeof t[k] === 'string' && t[k]) out[k] = t[k].slice(0, 200);
      }
      return Object.keys(out).length ? out : null;
    };
    const first = keep(raw.first);
    const last = keep(raw.last) || first;
    return first ? { first, last } : null;
  } catch {
    return null;
  }
}

/** Paddle's window must not sit under the cart drawer (seen in non-full-screen windows). */
function closeCartDrawer() {
  for (const drawer of document.querySelectorAll('theme-drawer')) {
    if (drawer.hasAttribute('open') || drawer.querySelector('dialog[open]')) {
      if (typeof drawer.close === 'function') drawer.close();
      else drawer.querySelector('dialog[open]')?.close();
      // The theme closes with an animation; if that stalls (e.g. animations paused), finish the close.
      window.setTimeout(() => drawer.querySelector('dialog[open]')?.close(), 600);
    }
  }
}

/**
 * Paddle events (forwarded by initPaddle in layout/theme.liquid as
 * "litelier:paddle"). Only a completed payment empties the cart: a failed
 * payment or a closed checkout keeps it, so the buyer can try again.
 */
function emptyCartAfterPayment() {
  fetch('/cart/clear.js', { method: 'POST', keepalive: true }).catch(() => {});
}

const PAID_TXN_KEY = 'lk-paid-txn';

document.addEventListener('litelier:paddle', (event) => {
  if (event.detail?.name !== 'checkout.completed') return;
  emptyCartAfterPayment();
  // T-455: the library page (successUrl) shows this order's files straight away.
  const txn = event.detail?.data?.transaction_id;
  if (txn) {
    try {
      window.localStorage.setItem(PAID_TXN_KEY, JSON.stringify({ txn, at: Date.now() }));
    } catch {}
  }
});

/**
 * The drawer re-renders (morphs) on every add/remove/quantity change, which
 * also resets the boxes this file fills, so any change, including text and
 * `hidden` resets inside those boxes, schedules a refresh. It can't loop:
 * every write above is skipped when the value is already on the page.
 */
function watchCart() {
  let scheduled = false;
  const observer = new MutationObserver((mutations) => {
    const relevant = mutations.some((m) => {
      const element = m.target instanceof Element ? m.target : m.target.parentElement;
      return !(element && element.closest('[data-cart-duplicate-note], [data-gift-picker]'));
    });
    if (!relevant || scheduled) return;
    scheduled = true;
    // A timer, not requestAnimationFrame: frames pause in background tabs.
    window.setTimeout(() => {
      scheduled = false;
      normalizeQuantities();
      renderCartSummary();
    }, 30);
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['hidden'] });
}

/**
 * After paying, Paddle sends the buyer to My Pattern Library with `?paid=1` (successUrl).
 * Strips the param so refreshing or sharing the link doesn't repeat it, empties the
 * cart and sets the cart count to 0.
 *
 * T-455 (Oct 22, report 5.4): instant download. The transaction id saved at
 * checkout.completed (same browser, under 2 hours old) is sent to the Worker
 * (GET /thanks/files?txn=…, paid + delivered orders only, 24 h), which answers with this
 * order's files; they show at once, before any sign-in. The webhook can land a few
 * seconds after the redirect, so "pending" is retried for up to a minute; if the files
 * still aren't there, the short thank-you line with the library link stays.
 */
const THANKS_FILES_URL = 'https://litelier-patterns-worker.litelier.workers.dev/thanks/files';
const THANKS_STAR = '<span class="lk-star" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false"><path d="M12 3.6l2.5 5.2 5.7.8-4.1 4 1 5.7L12 16.6l-5.1 2.7 1-5.7-4.1-4 5.7-.8z" fill="#C8922C" stroke="#C8922C" stroke-width="2.2" stroke-linejoin="round"/></svg></span>';

function libraryUrl() {
  return window.__litelierLibraryUrl || '/pages/my-pattern-library';
}

function setCartCountToZero() {
  const icon = document.querySelector('cart-icon');
  if (icon && typeof icon.renderCartBubble === 'function') icon.renderCartBubble(0, false);
}

/** Same "Review this pattern" link as the library cards (address list from my-pattern-library.liquid). */
function thanksReviewHtml(pattern) {
  const url = pattern.shopifyProductId ? (window.__lkReviewUrls || {})[String(pattern.shopifyProductId)] : null;
  if (!url) return '';
  return `<div class="pattern-library__review"><span>Loved it?</span> <a href="${escapeHtml(url)}#judgeme">Review this pattern 🌟</a></div>`;
}

function thanksPatternHtml(pattern, index) {
  const image = pattern.imageUrl
    ? `<img class="pattern-library__card-image" src="${escapeHtml(pattern.imageUrl)}" alt="" width="400" height="300">`
    : '';
  const sizes = pattern.sizes ? `<p class="pattern-library__card-sizes">Sizes ${escapeHtml(pattern.sizes)}</p>` : '';
  const files = pattern.files
    .map((file, f) => `<li><a href="${escapeHtml(file.url)}" class="pattern-library__file-link" data-lk-thanks-file="${index}:${f}">${escapeHtml(file.name)}</a></li>`)
    .join('');
  return `
    <div class="pattern-library__card lk-thanks__card">
      ${pattern.gift ? '<div class="pattern-library__card-top"><span class="pattern-library__badge pattern-library__badge--gift">Your free gift 🎁</span></div>' : ''}
      ${image}
      <h3 class="pattern-library__card-title">${escapeHtml(pattern.title)}</h3>
      ${sizes}
      <div class="pattern-library__card-actions">
        <div class="pattern-library__buttons">
          <a href="${escapeHtml(pattern.zipUrl)}" class="button pattern-library__zip-button" data-lk-thanks-zip="${index}">Download all (ZIP)</a>
          <button type="button" class="pattern-library__choose-files" data-lk-thanks-toggle aria-expanded="false">Choose files <span aria-hidden="true">▾</span></button>
        </div>
        <ol class="pattern-library__files" data-lk-thanks-list hidden>${files}</ol>
        <p class="pattern-library__zip-tip"><a href="/pages/help#get-files">How to open it</a></p>
      </div>
      ${thanksReviewHtml(pattern)}
    </div>`;
}

function renderThanks(panel, data) {
  const startHere = data.startHereUrl
    ? `<p class="lk-thanks__start"><a href="${escapeHtml(data.startHereUrl)}" data-lk-thanks-start>Start here: read me first</a></p>`
    : '';
  const who = data.emailHint ? ` with <strong>${escapeHtml(data.emailHint)}</strong>` : '';
  panel.innerHTML = `
    <h2 class="lk-thanks__title">Thank you ${THANKS_STAR} Your patterns are ready</h2>
    <p class="lk-thanks__lead">Download them now, right here.</p>
    ${startHere}
    <div class="pattern-library__grid lk-thanks__grid">${data.patterns.map(thanksPatternHtml).join('')}</div>
    <p class="lk-thanks__saved">Your patterns are also saved in <a href="${libraryUrl()}">My Pattern Library</a>. Sign in any time${who}: I email you a 6-digit code, no password needed.</p>`;
}

/** Links last 10 minutes: a click after that asks the Worker again, then follows the fresh link. */
function wireThanksLinks(panel, txn, state) {
  panel.addEventListener('click', (event) => {
    const toggle = event.target instanceof Element ? event.target.closest('[data-lk-thanks-toggle]') : null;
    if (!toggle) return;
    const list = toggle.closest('.lk-thanks__card')?.querySelector('[data-lk-thanks-list]');
    if (!list) return;
    list.hidden = !list.hidden;
    toggle.setAttribute('aria-expanded', String(!list.hidden));
  });
  panel.addEventListener('click', async (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-lk-thanks-zip], [data-lk-thanks-file], [data-lk-thanks-start]') : null;
    if (!link || Date.now() < state.data.expiresAt - 30000) return;
    event.preventDefault();
    const fresh = await fetchThanks(txn);
    if (!fresh || fresh.status !== 'ready') {
      window.location.href = libraryUrl();
      return;
    }
    state.data = fresh;
    let href = null;
    if (link.hasAttribute('data-lk-thanks-start')) href = fresh.startHereUrl;
    else if (link.hasAttribute('data-lk-thanks-zip')) href = fresh.patterns[Number(link.getAttribute('data-lk-thanks-zip'))]?.zipUrl;
    else {
      const [p, f] = link.getAttribute('data-lk-thanks-file').split(':').map(Number);
      href = fresh.patterns[p]?.files[f]?.url;
    }
    window.location.href = href || libraryUrl();
  });
}

async function fetchThanks(txn) {
  try {
    const response = await fetch(`${THANKS_FILES_URL}?txn=${encodeURIComponent(txn)}`, { cache: 'no-store' });
    return await response.json();
  } catch {
    return null;
  }
}

async function showInstantDownload(txn, panel) {
  const started = Date.now();
  while (Date.now() - started < 60000) {
    const data = await fetchThanks(txn);
    if (data && data.status === 'ready' && data.patterns?.length) {
      const state = { data };
      renderThanks(panel, data);
      wireThanksLinks(panel, txn, state);
      // The files are right here, so the sign-in steps under them only confuse (Gina, Oct 23 test).
      document.querySelectorAll('.lk-lib-signin').forEach((el) => { el.hidden = true; });
      return;
    }
    if (!data || data.status !== 'pending') return;
    await new Promise((resolve) => setTimeout(resolve, 3000));
  }
}

function takePaidTransaction() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(PAID_TXN_KEY) || 'null');
    window.localStorage.removeItem(PAID_TXN_KEY);
    if (saved && saved.txn && Date.now() - saved.at < 2 * 60 * 60 * 1000) return saved.txn;
  } catch {}
  return null;
}

function handlePaddleReturn() {
  const url = new URL(window.location.href);
  if (url.searchParams.get('paid') !== '1') return;
  const transactionId = takePaidTransaction();

  window.__lkJustPaid = true;
  emptyCartAfterPayment();
  setCartCountToZero();
  const panel = document.createElement('section');
  panel.className = 'paddle-return-banner lk-thanks';
  panel.setAttribute('role', 'status');
  panel.setAttribute('aria-live', 'polite');
  panel.innerHTML = `
    <p>Thank you ${THANKS_STAR} your order is complete. Getting your patterns ready…</p>
    <p class="lk-thanks__saved">They are also saved in <a href="${libraryUrl()}">My Pattern Library</a>.</p>`;
  const library = document.querySelector('.pattern-library');
  if (library) library.prepend(panel);
  else document.body.prepend(panel);

  url.searchParams.delete('paid');
  window.history.replaceState({}, '', url.toString());

  (transactionId ? showInstantDownload(transactionId, panel) : Promise.resolve()).then(() => {
    if (panel.querySelector('.lk-thanks__grid')) return;
    panel.innerHTML = `<p>Thank you ${THANKS_STAR} your order is complete. Your patterns are ready in <a href="${libraryUrl()}">My Pattern Library</a>.</p>`;
  });
}

document.addEventListener('DOMContentLoaded', handlePaddleReturn);
// Paddle.js may finish loading after the first render; re-price once it can preview.
window.addEventListener('load', () => renderCartSummary());
normalizeQuantities();
renderCartSummary();
watchCart();
